import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaFileAlt, 
  FaCheckCircle, 
  FaUpload, 
  FaClock, 
  FaPassport,
  FaPlane,
  FaUserCheck,
  FaCertificate,
  FaCalendarCheck,
  FaQuestionCircle,
  FaDownload,
  FaVideo,
  FaPhone,
  FaEnvelope
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const ProcessPage = () => {
  const [activeStep, setActiveStep] = useState(0);

  const processSteps = [
    {
      id: 1,
      icon: <FaFileAlt className="text-2xl" />,
      title: 'Initial Consultation',
      description: 'Free assessment of your visa eligibility',
      details: [
        '15-minute free consultation',
        'Visa eligibility assessment',
        'Personalized strategy',
        'Document checklist'
      ],
      duration: '1-2 Days',
      important: 'Be prepared to discuss your travel history and purpose'
    },
    {
      id: 2,
      icon: <FaCheckCircle className="text-2xl" />,
      title: 'Document Preparation',
      description: 'Gathering and verifying all required documents',
      details: [
        'Passport verification',
        'Financial documents',
        'Supporting letters',
        'Proof of accommodation'
      ],
      duration: '3-7 Days',
      important: 'We review every document for accuracy'
    },
    {
      id: 3,
      icon: <FaUpload className="text-2xl" />,
      title: 'Application Submission',
      description: 'Filing your application with authorities',
      details: [
        'Online form completion',
        'Appointment scheduling',
        'Biometrics collection',
        'Fee payment'
      ],
      duration: '1-3 Days',
      important: 'Timing is crucial for appointment slots'
    },
    {
      id: 4,
      icon: <FaClock className="text-2xl" />,
      title: 'Processing & Follow-up',
      description: 'Tracking and monitoring your application',
      details: [
        'Regular status updates',
        'Government liaison',
        'Additional information requests',
        'Interview preparation'
      ],
      duration: 'Varies by visa type',
      important: 'Response time depends on embassy workload'
    },
    {
      id: 5,
      icon: <FaCertificate className="text-2xl" />,
      title: 'Decision & Collection',
      description: 'Receiving your visa outcome',
      details: [
        'Visa approval notification',
        'Passport collection',
        'Visa stamping',
        'Final briefing'
      ],
      duration: '2-5 Days',
      important: 'Review visa validity and conditions'
    },
    {
      id: 6,
      icon: <FaPlane className="text-2xl" />,
      title: 'Post-Approval Support',
      description: 'Assistance after visa approval',
      details: [
        'Travel preparation guidance',
        'Insurance arrangements',
        'Arrival procedures',
        'Extension possibilities'
      ],
      duration: 'Ongoing',
      important: 'Keep copies of all documents for entry'
    }
  ];

  const documentChecklists = {
    tourist: [
      'Valid passport (6+ months validity)',
      'Completed application form',
      'Passport-sized photos',
      'Flight itinerary',
      'Hotel reservations',
      'Travel insurance',
      'Bank statements (3-6 months)',
      'Employment verification'
    ],
    student: [
      'University admission letter',
      'Valid passport',
      'Academic transcripts',
      'English proficiency test',
      'Financial guarantee',
      'Health insurance',
      'Statement of purpose',
      'Proof of funds'
    ],
    schengen: [
      'Schengen application form',
      'Valid passport',
      'Travel medical insurance',
      'Accommodation proof',
      'Round-trip tickets',
      'Cover letter',
      'Financial means proof',
      'Employment letter'
    ],
    family: [
      'Relationship proof',
      'Sponsor documents',
      'Valid passports',
      'Marriage/birth certificates',
      'Accommodation proof',
      'Financial support evidence',
      'Medical insurance',
      'Police clearance'
    ]
  };

  const processingTimes = [
    { type: 'Tourist Visa', standard: '5-15 days', express: '2-5 days' },
    { type: 'Student Visa', standard: '4-8 weeks', express: '2-4 weeks' },
    { type: 'Schengen Visa', standard: '10-20 days', express: '3-7 days' },
    { type: 'Family Visa', standard: '3-12 months', express: '1-6 months' },
    { type: 'Business Visa', standard: '5-15 days', express: '1-3 days' }
  ];

  const [activeDocumentType, setActiveDocumentType] = useState('tourist');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-400 rounded-full blur-3xl opacity-20"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary-300 rounded-full blur-3xl opacity-20"></div>
        </div>
        
        <div className="relative container-padding py-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-bold mb-6"
            >
              Our Process
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-primary-100 mb-8"
            >
              A transparent, step-by-step journey to your visa approval
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3"
            >
              <FaUserCheck className="mr-3" />
              <span className="font-medium">95% Success Rate • 10,000+ Successful Applications</span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              6-Step Visa Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From consultation to approval, we guide you through every step
            </p>
          </div>

          {/* Process Timeline */}
          <div className="relative">
            {/* Timeline line for desktop */}
            <div className="hidden lg:block absolute left-0 right-0 top-12 h-1 bg-gray-200"></div>
            
            <div className="grid lg:grid-cols-6 gap-8">
              {processSteps.map((step, index) => (
                <motion.button
                  key={step.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => setActiveStep(index)}
                  className="relative text-center group"
                >
                  {/* Connector line for mobile */}
                  {index < processSteps.length - 1 && (
                    <div className="lg:hidden absolute top-6 left-1/2 w-full h-1 bg-gray-200 -z-10"></div>
                  )}
                  
                  {/* Step circle */}
                  <div className={`relative z-10 w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center transition-all ${
                    activeStep === index
                      ? 'bg-primary-600 text-white scale-110 shadow-xl'
                      : 'bg-white text-gray-700 shadow-lg group-hover:bg-primary-100 group-hover:scale-105'
                  }`}>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full border-4 border-gray-50 flex items-center justify-center text-primary-600 font-bold shadow">
                      {step.id}
                    </div>
                    {step.icon}
                  </div>
                  
                  <h3 className={`font-bold text-lg mb-2 ${
                    activeStep === index ? 'text-primary-600' : 'text-gray-900'
                  }`}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {step.duration}
                  </p>
                </motion.button>
              ))}
            </div>

            {/* Step Details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-12 bg-white rounded-2xl shadow-xl p-8"
              >
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600 mr-4">
                        {processSteps[activeStep].icon}
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {processSteps[activeStep].title}
                        </h3>
                        <p className="text-gray-600">
                          {processSteps[activeStep].description}
                        </p>
                      </div>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                      {processSteps[activeStep].details.map((detail, idx) => (
                        <li key={idx} className="flex items-center text-gray-700">
                          <FaCheckCircle className="mr-3 text-green-500 flex-shrink-0" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                    
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                      <div className="flex">
                        <FaQuestionCircle className="text-yellow-500 mr-3 mt-1 flex-shrink-0" />
                        <div>
                          <h4 className="font-bold text-yellow-800 mb-1">
                            Important Note
                          </h4>
                          <p className="text-yellow-700">
                            {processSteps[activeStep].important}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-gray-900 mb-4">
                      Estimated Timeline
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-gray-700">Progress</span>
                          <span className="text-primary-600 font-medium">
                            Step {activeStep + 1} of {processSteps.length}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${((activeStep + 1) / processSteps.length) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="pt-4 border-t border-gray-200">
                        <div className="flex items-center text-gray-700 mb-2">
                          <FaClock className="mr-3 text-primary-600" />
                          <span className="font-medium">Duration:</span>
                          <span className="ml-2">{processSteps[activeStep].duration}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <FaCalendarCheck className="mr-3 text-primary-600" />
                          <span className="font-medium">Next Step:</span>
                          <span className="ml-2">
                            {activeStep < processSteps.length - 1 
                              ? processSteps[activeStep + 1].title 
                              : 'Completion'}
                          </span>
                        </div>
                      </div>
                      
                      <button className="w-full mt-6 btn-primary">
                        Start This Step
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Document Checklists */}
      <section className="py-20 bg-white">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Document Checklists
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Essential documents required for different visa types
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {/* Visa Type Tabs */}
            <div className="flex flex-wrap gap-2 mb-8">
              {Object.keys(documentChecklists).map((type) => (
                <button
                  key={type}
                  onClick={() => setActiveDocumentType(type)}
                  className={`px-6 py-3 rounded-lg font-medium transition ${
                    activeDocumentType === type
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)} Visa
                </button>
              ))}
            </div>
            
            {/* Document List */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="grid md:grid-cols-2 gap-6">
                {documentChecklists[activeDocumentType].map((doc, index) => (
                  <motion.div
                    key={doc}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center bg-white rounded-lg p-4 shadow-sm"
                  >
                    <FaFileAlt className="text-primary-600 mr-4 flex-shrink-0" />
                    <span className="text-gray-700">{doc}</span>
                    <span className="ml-auto text-sm text-gray-500">Required</span>
                  </motion.div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="flex items-center text-gray-700">
                  <FaDownload className="mr-2" />
                  <span>Download complete checklist</span>
                </div>
                <button className="btn-primary">
                  Download PDF Checklist
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Processing Times */}
      <section className="py-20 bg-gray-50">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Processing Times
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Estimated processing durations for different visa types
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-4 px-6 text-left font-bold text-gray-900">Visa Type</th>
                      <th className="py-4 px-6 text-left font-bold text-gray-900">Standard Processing</th>
                      <th className="py-4 px-6 text-left font-bold text-gray-900">Express Processing</th>
                      <th className="py-4 px-6 text-left font-bold text-gray-900">Success Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {processingTimes.map((item, index) => (
                      <tr key={item.type} className="hover:bg-gray-50 transition">
                        <td className="py-4 px-6 font-medium text-gray-900">
                          {item.type}
                        </td>
                        <td className="py-4 px-6 text-gray-700">
                          {item.standard}
                        </td>
                        <td className="py-4 px-6 text-gray-700">
                          {item.express}
                        </td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                            <FaCheckCircle className="mr-1" />
                            94-98%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="p-6 bg-primary-50 border-t border-primary-100">
                <div className="flex flex-col md:flex-row justify-between items-center">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2">Need Faster Processing?</h4>
                    <p className="text-gray-700">
                      Ask about our premium express services for urgent applications
                    </p>
                  </div>
                  <button className="btn-primary mt-4 md:mt-0">
                    Inquire About Express Service
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-white">
        <div className="container-padding">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Need Help With Your Process?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're here to guide you through every step of your visa journey
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-8 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                <FaVideo className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Video Consultation
              </h3>
              <p className="text-gray-600 mb-6">
                Schedule a one-on-one video call with our visa experts
              </p>
              <button className="btn-primary w-full">
                Schedule Call
              </button>
            </div>
            
            <div className="text-center p-8 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                <FaPhone className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Phone Support
              </h3>
              <p className="text-gray-600 mb-6">
                Call us directly for immediate assistance
              </p>
              <a href="tel:+15551234567" className="btn-primary w-full inline-block">
                Call Now
              </a>
            </div>
            
            <div className="text-center p-8 bg-gray-50 rounded-2xl hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary-100 rounded-full flex items-center justify-center text-primary-600">
                <FaEnvelope className="text-2xl" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Email Support
              </h3>
              <p className="text-gray-600 mb-6">
                Send us your questions and documents for review
              </p>
              <a href="mailto:support@globalvisa.com" className="btn-primary w-full inline-block">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900 to-primary-700 text-white">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Start Your Visa Process?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto text-primary-100">
              Begin your journey with a free consultation and personalized strategy
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary bg-white text-primary-700 hover:bg-primary-100 px-8 py-4 text-lg"
              >
                Begin Application
              </Link>
              <Link
                to="/contact"
                className="btn-secondary border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Free Process Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProcessPage;